import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import { Box, Chip, Typography } from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ColorfulRole from "../../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import type { ShiftCategory, StaffMemberDailyBreakdown } from "../../../utils/models";
import { MC_GRID_SX } from "../utils";

const SHIFT_CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
	Morning: { bg: "#FFF7ED", text: "#C2410C" },
	Day: { bg: "#F0F9FF", text: "#0284C7" },
	Evening: { bg: "#FFFBEB", text: "#D97706" },
	Night: { bg: "#EEF2FF", text: "var(--inhouse-primary-dark)" },
};

const SHIFT_ICONS: Record<string, typeof WbSunnyOutlinedIcon> = {
	Morning: LightModeOutlinedIcon,
	Day: WbSunnyOutlinedIcon,
	Evening: WbTwilightIcon,
	Night: NightsStayOutlinedIcon,
};

const BADGE_COLORS = {
	shift_extended: { bg: "#FFF7ED", text: "#C2410C", border: "#FDBA74" },
	different_shift: { bg: "#F0FDFA", text: "#0F766E", border: "#99F6E4" },
	unscheduled: { bg: "#FFFBEB", text: "#D97706", border: "#FCD34D" },
	late_scheduled: { bg: "#FFF7ED", text: "#9A3412", border: "#FDBA74" },
	weekly_ot: { bg: "#F0F9FF", text: "#0369A1", border: "#7DD3FC" },
};

type Props = {
	staff: StaffMemberDailyBreakdown[];
};

export const ScheduleVsActualProblemsView: FC<Props> = ({ staff }) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();

	const problematicStaff = useMemo(
		() => staff.filter((s) => !s.replacedCallOff && (s.isExtendedHours || s.isDifferentShift || s.isUnscheduled || s.isLateScheduled || s.weeklyOtHours > 0)),
		[staff],
	);

	const columns: GridColDef<StaffMemberDailyBreakdown>[] = useMemo(
		() => [
			{
				field: "role",
				headerName: t("schedule.vs.actual.role"),
				flex: 0.4,
				display: "flex",
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => <ColorfulRole role={params.row.role} />,
			},
			{
				field: "name",
				headerName: t("schedule.vs.actual.name"),
				flex: 1,
				valueGetter: (_value: unknown, row: StaffMemberDailyBreakdown) => formatPersonName(row),
				sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
			},
			{
				field: "shiftCategory",
				headerName: t("schedule.vs.actual.shift.type"),
				flex: 1,
				display: "flex",
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => {
					const category = params.row.shiftCategory;
					if (!category) return null;
					const colors = SHIFT_CATEGORY_COLORS[category] ?? SHIFT_CATEGORY_COLORS.Day;
					const Icon = SHIFT_ICONS[category] ?? WbSunnyOutlinedIcon;
					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Icon sx={{ fontSize: 16, color: colors.text }} />
							{params.row.shiftHoursLabel && (
								<Typography variant="caption" sx={{ opacity: 0.7 }}>
									{params.row.shiftHoursLabel}
								</Typography>
							)}
						</Box>
					);
				},
			},
			{
				field: "badges",
				headerName: t("schedule.vs.actual.badges"),
				flex: 1.5,
				display: "flex",
				sortable: false,
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => {
					const badges: { label: string; colors: (typeof BADGE_COLORS)[keyof typeof BADGE_COLORS] }[] = [];
					if (params.row.isExtendedHours) {
						const diff = Math.round((params.row.actualHours - params.row.scheduledHours) * 10) / 10;
						badges.push({
							label: t("schedule.vs.actual.extended.hours", { hours: diff }),
							colors: BADGE_COLORS.shift_extended,
						});
					}
					if (params.row.isDifferentShift) {
						badges.push({
							label: t("schedule.vs.actual.different.shift"),
							colors: BADGE_COLORS.different_shift,
						});
					}
					if (params.row.isUnscheduled) {
						badges.push({
							label: t("schedule.vs.actual.unscheduled"),
							colors: BADGE_COLORS.unscheduled,
						});
					}
					if (params.row.isLateScheduled) {
						badges.push({
							label: t("alerts.insight.late.scheduled"),
							colors: BADGE_COLORS.late_scheduled,
						});
					}
					if (params.row.weeklyOtHours > 0) {
						badges.push({
							label: t("schedule.vs.actual.weekly.ot", { hours: params.row.weeklyOtHours }),
							colors: BADGE_COLORS.weekly_ot,
						});
					}
					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								flexWrap: "wrap",
							}}
						>
							{badges.map((badge) => (
								<Chip
									key={badge.label}
									label={badge.label}
									size="small"
									variant="outlined"
									sx={{
										borderColor: badge.colors.border,
										color: badge.colors.text,
										bgcolor: badge.colors.bg,
										fontWeight: 500,
										fontSize: "0.65rem",
										height: 22,
									}}
								/>
							))}
						</Box>
					);
				},
			},
		],
		[t, formatPersonName],
	);

	if (problematicStaff.length === 0) {
		return (
			<Box
				sx={{
					py: 2,
					px: 1,
				}}
			>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
					}}
				>
					{t("schedule.vs.actual.problems.none")}
				</Typography>
			</Box>
		);
	}

	return (
		<DataGrid
			rows={problematicStaff}
			columns={columns}
			getRowId={(row) => row.nurseId}
			density="compact"
			disableColumnMenu
			disableColumnFilter
			hideFooter
			rowSelection={false}
			sx={{
				...MC_GRID_SX,
				bgcolor: "transparent",
			}}
		/>
	);
};
